import React, {useCallback, useEffect, useReducer} from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import Select from "react-select";
import NumberFormat from "react-number-format";
import {toast} from "react-toastify";
import {Datepicker, momentTimezone} from "@mobiscroll/react";
import moment from "moment";

import ai from "../../helpers/axios";
import Loader from "../loader";
import currencyFormatter from "../../helpers/currencyFormatter";
import StateDisplay from "../reusable-modules/state-display";
import EmailInput from "../reusable-modules/email-input";
import TextAreaInput from "../textarea-input";
import TextInput from "../text-input";
import {LoadingButton} from "../shared/loading-button";
import PageHeader from "../shared/page-header";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";
import { isTripLockedForUser } from "../../helpers/is-trip-editable";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import { formatLegDateLabel } from "../../helpers/dates";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "modalFieldChanged": {
      return {
        ...state,
        modalData: {
          ...state.modalData,
          [action.field]: action.value,
        },
      };
    }

    case "recipientDetailFieldChanged": {
      return {
        ...state,
        modalData: {
          ...state.modalData,
          recipientDetail: {
            ...state.modalData?.recipientDetail,
            [action.field]: action.value,
          },
        },
      };
    }

    case "paymentDetailFieldChanged": {
      return {
        ...state,
        modalData: {
          ...state.modalData,
          paymentDetail: {
            ...state.modalData?.paymentDetail,
            [action.field]: action.value,
          },
        },
      };
    }

    case "leadDesignationFieldChanged": {
      return {
        ...state,
        modalData: {
          ...state.modalData,
          leadDesignation: {
            ...state.modalData?.leadDesignation,
            [action.field]: action.value,
          },
        },
      };
    }

    case "contractOFRDetailFieldChanged": {
      return {
        ...state,
        modalData: {
          ...state.modalData,
          contractOFRDetail: {
            ...state.modalData?.contractOFRDetail,
            [action.field]: action.value,
          },
        },
      };
    }

    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        loading: false,
        contractHTML: action?.payload?.data?.html,
        pdfData: action?.payload?.data?.pdf,
        contractRecord: action?.payload?.data?.contractRecord,
        fileName: action?.payload?.data?.fileName,
        tripTag: action?.payload?.data?.contractRecord?.tripData?.tripTag ?? '',
      };
    }

    case "pdfDownloaded": {
      return {
        ...state,
        pdfData: action.payload.data,
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }

    default:
      break;
  }
  return state;
};

const ContractPreviewContent = (props) => {
  const { getAccessTokenSilently, user, loginWithRedirect } = useAuth0();
  const { tripId, contractId } = useParams();

  const userRoles = user && user["https://new.evojets.com/roles"];

  const superAdmin = userRoles.some(function (role) {
    return "Super Admin" === role;
  });

  const verificationAdmin = !!user?.["https://new.evojets.com/appMetadata"]?.isVerificationAdmin;

  const accountDirector = userRoles.some(function (role) {
    return "Account Director" === role;
  });

  // Auth0 app_metadata flags. The server enforces the same rules
  // (server/services/trips/contractActionPolicy.js); these only drive the UI.
  const isCcApprover =
    user?.["https://new.evojets.com/appMetadata"]?.isCcApprover === true;
  const isHighDollarApprover100k =
    user?.["https://new.evojets.com/appMetadata"]?.isHighDollarApprover100k === true;
  const isHighDollarApprover =
    isHighDollarApprover100k ||
    user?.["https://new.evojets.com/appMetadata"]?.isHighDollarApprover === true;
  const isMarkSentAllowed =
    user?.["https://new.evojets.com/appMetadata"]?.isMarkSentAllowed === true;
  const isMarkSentBlocked =
    user?.["https://new.evojets.com/appMetadata"]?.isMarkSentBlocked === true;

  let initialState = {
    loading: true,
    modalTitle: "",
    showModal: false,
    action: null,
    selectedOFRs: [],
    logic: {
      isCCTrip: null,
      is50KTrip: null,
      is100KTrip: null,
      isHighDollarTrip: null,
      canRequestDollarApproval: null,
      ofrExists: null,
      isOFA: null,
      isSent: null,
      isSigned: null,
      isFOW: null,
      isCTF: null,
      isAppManagedContract: null,
      isSaved: null,
      isCCApproved: null,
      isHighDollarApproved: null,
      isInReview: null,
      isApproved: null,
      isSafeToSend: null,
      contractInstructions: null,
      canMarkSent: null,
    },
    isUpdating: false,
    isMarkUnsignedUpdating: false,
    isSyncingToApp: false,
    leadData: {},
    tripTag: "",
    unsavedChanges: {},
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const readOnly = isTripLockedForUser(
    state.contractRecord?.tripData,
    superAdmin,
    verificationAdmin,
  );

  const setUnsavedChanges = useCallback(({field, value}) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value
    })
  }, []);

  useEffect(() => {
    if (state.modalTitle === "Request To Send") {
      setUnsavedChanges({
        field: 'modal',
        value: state.showModal,
      });
    }
  }, [setUnsavedChanges, state.modalTitle, state.showModal]);

  const getData = async (loadSilently = false) => {
    if (!loadSilently) {
      dispatch({type: "dataBeganDownloading"});
    }

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/contracts/${contractId}/contractPreview`)
      .catch((error) => {
        setTimeout(() => {
          toast.error("Unauthorized!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    let leadId = response?.data?.contractRecord?.tripData?.leadId;
    if (leadId) {
      let leadResponse = await ai
        .auth(token)
        .get(`/api/leads/${leadId}`)
        .catch((error) => {
          console.error(error.response);
        });

      if (leadResponse?.data) {
        dispatch({
          type: "fieldChanged",
          field: "leadData",
          value: leadResponse?.data,
        });
      }
    }

    if (response) {
      dispatch({
        type: "dataDownloaded",
        payload: {
          data: response.data,
        },
      });

      handleConditionalLogic(response?.data?.contractRecord);
    }
  };

  const handleConditionalLogic = (contractData) => {
    if (!contractData || !contractData?.tripData) {
      return false;
    }

    let isCCTrip = contractData?.pricing?.paymentType === "cc";

    let is50KTrip =
      contractData?.pricing?.flightCharges >= 50000 &&
      contractData?.pricing?.flightCharges < 100000;
    let is100KTrip = contractData?.pricing?.flightCharges >= 100000;
    let isHighDollarTrip = is50KTrip || is100KTrip;

    let isHighDollarApproved = contractData.highDollarApproval;

    if (!isHighDollarTrip) {
      isHighDollarApproved = true;
    }

    // isHighDollarApprover covers $50k–$100k; $100k+ needs isHighDollarApprover100k.
    const canApproveDollarAmount = is100KTrip
      ? isHighDollarApprover100k
      : isHighDollarApprover;
    let canRequestDollarApproval =
      isHighDollarTrip && !isHighDollarApproved && !canApproveDollarAmount;

    let ofrExists = !!contractData?.operatorFinancialRecords?.length;

    let isOFA = contractData?.stage === "ofa";

    let isFOW = !!contractData?.contractRecord?.featuredOneWayId;
    let isCTF = !!contractData?.contractRecord?.clickToFlyResultId;
    let isAppManagedContract = isFOW || isCTF;

    let isSent = contractData?.stage === "sent";
    let isSigned = contractData?.stage === "signed";
    let hasRetrievableSignedPdf =
      isSigned && !!contractData?.dropboxSign?.signatureRequestId;
    let isSaved = contractData?.stage === "saved";
    let isApproved = contractData?.stage === "approved";

    let isCCApproved = contractData.ccApproval;

    let isInReview = contractData?.stage === "inReview";

    // Super Admins and ADs may mark any contract sent; others only their own,
    // with isMarkSentAllowed. isMarkSentBlocked overrides every grant.
    const isOwner =
      !!user?.sub &&
      contractData?.owner?.id &&
      user.sub === contractData.owner.id;
    const canMarkSent =
      !isMarkSentBlocked &&
      (superAdmin || accountDirector || (isMarkSentAllowed && isOwner));

    //isSafeToSend is true if the contract is not yet sent or signed, and if it's a high dollar trip, it's been approved, and if it's a CC trip, it's been CC approved
    let isSafeToSend = !isSent && !isSigned;

    if (isHighDollarTrip) {
      isSafeToSend = isSafeToSend && isHighDollarApproved;
    }
    if (isCCTrip) {
      isSafeToSend = isSafeToSend && isCCApproved;
    }

    if (!contractData?.operatorFinancialRecords?.length) {
      isSafeToSend = false;
    }

    let requiresCCApproval = isCCTrip && !isCCApproved;
    let requiresDollarApproval = isHighDollarTrip && !isHighDollarApproved;

    let contractInstructions = ``;

    if (requiresCCApproval || requiresDollarApproval || !ofrExists) {
      contractInstructions = (
        <React.Fragment>
          <p>
            <strong>This trip requires the following:</strong>
          </p>
          <ul>
            {!ofrExists && <li>At least one OFR</li>}
            {requiresCCApproval && <li>CC Approval</li>}
            {requiresDollarApproval && <li>High Dollar Approval</li>}
          </ul>
        </React.Fragment>
      );
    }

    if (contractData?.associatedOFRIds?.length) {
      //get matching OFRs from contractData?.operatorFinancialRecords
      let matchingOFRs = contractData?.operatorFinancialRecords.filter(
        (ofr) => {
          return contractData?.associatedOFRIds.includes(ofr?._id);
        }
      );

      if (matchingOFRs?.length) {
        let matchingOFROptions = matchingOFRs.map((ofr) => {
          return {
            label: `#${ofr?.ofrTag} | ${ofr?.operator?.name} | ${
              ofr?.operatorPrice
                ? currencyFormatter.format(ofr?.operatorPrice)
                : "No Operator Price"
            }`,
            value: ofr?._id,
          };
        });

        dispatch({
          type: "fieldChanged",
          field: "selectedOFRs",
          value: matchingOFROptions,
        });
      }
    }

    dispatch({
      type: "fieldChanged",
      field: "logic",
      value: {
        isCCTrip,
        is50KTrip,
        is100KTrip,
        isHighDollarTrip,
        canRequestDollarApproval,
        ofrExists,
        isOFA,
        isSent,
        isSigned,
        hasRetrievableSignedPdf,
        isFOW,
        isCTF,
        isAppManagedContract,
        isSaved,
        isCCApproved,
        isInReview,
        isApproved,
        isSafeToSend,
        contractInstructions,
        isHighDollarApproved,
        requiresCCApproval,
        canMarkSent,
      },
    });
  };

  const handlePDF = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/contracts/${contractId}/pdf`, {
        responseType: "arraybuffer",
      })
      .catch((error) => {
        setTimeout(() => {
          toast.error("Unauthorized!", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    if (response) {
      dispatch({
        type: "pdfDownloaded",
        payload: {
          data: response.data,
        },
      });

      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      let link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${state.fileName ? state.fileName : `evo-contract.pdf`}`
      );
      document.body.appendChild(link);
      link.click();
    }
  };

  const handleSignedPDF = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .get(`/api/trips/${tripId}/contracts/${contractId}/signed-pdf`, {
        responseType: "arraybuffer",
      })
      .catch((error) => {
        setTimeout(() => {
          toast.error("Unable to download signed contract.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        }, 50);

        return console.error(error.response);
      });

    if (response) {
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );

      const baseName = state.fileName ? state.fileName : "evo-contract.pdf";
      const signedName = baseName.replace(/(\.pdf)?$/i, " - SIGNED.pdf");

      let link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", signedName);
      document.body.appendChild(link);
      link.click();
    }
  };

  const handleAssociatedOFRChange = () => {
    let selectedOFRs = state.selectedOFRs?.map((ofr) => {
      return state.contractRecord?.operatorFinancialRecords.find((record) => {
        return record?._id === ofr?.value;
      });
    }) || [];

    //for each OFR, get the following
    // operator name
    //dusCancellationPolicy

    let operatorBreakdown = selectedOFRs.flatMap((ofr) => {
      if (!ofr) {
        return [];
      }

      let dusCancellationPolicy = ``;

      if (ofr?.dusCancellationPolicy?.value === 0) {
        dusCancellationPolicy = `No Fee`;
      } else {
        let type =
          ofr?.dusCancellationPolicy?.type === 1
            ? "%"
            : ofr?.dusCancellationPolicy?.type === 2
            ? "$"
            : false;
        if (type === "%") {
          dusCancellationPolicy = `${ofr?.dusCancellationPolicy?.value}${type}`;
        } else if (type === "$") {
          dusCancellationPolicy = `${type}${ofr?.dusCancellationPolicy?.value}`;
        }
      }

      let remainingPenalties = ofr?.operatorCancellationPolicies.flatMap(
        (policy) => {
          if (policy?.dueDate && policy.value !== 0 && policy.type) {
            let type =
              policy?.type === 1 ? "%" : policy?.type === 2 ? "$" : false;

            let formattedDate = moment(policy?.dueDate).format("MM/DD/YYYY");

            if (type === "%") {
              return `${formattedDate} - ${policy?.value}${type}`;
            }

            if (type === "$") {
              let formattedValue = currencyFormatter.format(policy?.value);
              return `${formattedDate} - ${formattedValue}`;
            }
          }

          return [];
        }
      );

      return {
        operatorName: ofr?.operator?.name,
        fet: ofr?.fet?.label,
        dusCancellationPolicy: dusCancellationPolicy,
        remainingPenalties: remainingPenalties,
      };
    });

    //update paymentDetail.operatorPrices

    let operatorPrices = selectedOFRs.map((ofr) => {
      return {
        operatorName: ofr?.operator?.name,
        operatorPrice: ofr?.operatorPrice,
      };
    });

    dispatch({
      type: "fieldChanged",
      field: "modalData",
      value: {
        ...state.modalData,
        contractOFRDetail: {
          ...state.modalData?.contractOFRDetail,
          operatorBreakdown,
        },
        paymentDetail: {
          ...state.modalData?.paymentDetail,
          operatorPrices,
        },
      },
    });
  };

  useEffect(() => {
    handleAssociatedOFRChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedOFRs]);

  const handleContractModal = (action) => {
    let actionsRequiringModal = [
      "requestToSend",
      "markSentNotRequested",
      "syncToAppNotRequested",
      "requestCC",
      "requestDollarApproval50k",
      "requestDollarApproval100k",
      "markSent",
      "markSigned",
      "archiveEvoContract",
      "revokeSendRequest",
      "highDollarApproval50k",
      "highDollarApproval100k",
      "syncToAppAndMarkSent",
    ];

    if (!actionsRequiringModal.includes(action)) {
      return false;
    }

    dispatch({
      type: "fieldChanged",
      field: "modalButtonText",
      value: "Submit",
    });

    let legDates = ``;
    let routingString = ``;

    //get leg one departure date
    //get last leg departure date IF there is more than one leg
    // format as MM/DD/YYYY - MM/DD/YYYY (or just MM/DD/YYYY if there's only one leg)

    if (state.contractRecord?.legs?.length) {
      let firstLegDepartureDate = formatLegDateLabel(state.contractRecord?.legs[0]);

      let lastLegDepartureDate = formatLegDateLabel(
        state.contractRecord?.legs[state.contractRecord?.legs?.length - 1]
      );

      if (state.contractRecord?.legs?.length === 1) {
        legDates = firstLegDepartureDate;
      } else {
        legDates = `${firstLegDepartureDate} - ${lastLegDepartureDate}`;
      }

      //get departure airport code for each leg, stitched together by -

      let departureAirportCodes = state.contractRecord?.legs.map((leg) => {
        return leg?.departureAirport?.code;
      });

      if (state.contractRecord?.legs?.length > 3) {
        //first departure airport code
        routingString = `${departureAirportCodes[0]}-Multi`;
      }

      if (
        state.contractRecord?.legs?.length <= 3 &&
        state.contractRecord?.legs?.length > 1
      ) {
        //push last arrival airport code to departureAirportCodes
        departureAirportCodes.push(
          state.contractRecord?.legs[state.contractRecord?.legs?.length - 1]
            ?.arrivalAirport?.code
        );

        routingString = departureAirportCodes.join("-");
      }

      if (state.contractRecord?.legs?.length === 1) {
        routingString = `${departureAirportCodes[0]} - ${state.contractRecord?.legs[0]?.arrivalAirport?.code}`;
      }
    }

    //get OFRs from state.contractRecord?.operatorFinancialRecords using state.selectedOFRs

    let selectedOFRs = state.selectedOFRs?.map((ofr) => {
      return state.contractRecord?.operatorFinancialRecords.find((record) => {
        return record?._id === ofr?.value;
      });
    }) || [];

    //for each OFR, get the following
    // operator name
    //dusCancellationPolicy

    let operatorBreakdown = selectedOFRs.flatMap((ofr) => {
      if (!ofr) {
        return [];
      }

      let dusCancellationPolicy = ``;

      if (ofr?.dusCancellationPolicy?.value === 0) {
        dusCancellationPolicy = `No Fee`;
      } else {
        let type =
          ofr?.dusCancellationPolicy?.type === 1
            ? "%"
            : ofr?.dusCancellationPolicy?.type === 2
            ? "$"
            : false;
        if (type === "%") {
          dusCancellationPolicy = `${ofr?.dusCancellationPolicy?.value}${type}`;
        } else if (type === "$") {
          dusCancellationPolicy = `${type}${ofr?.dusCancellationPolicy?.value}`;
        }
      }

      let remainingPenalties = ofr?.operatorCancellationPolicies.flatMap(
        (policy) => {
          if (policy?.dueDate && policy.value !== 0 && policy.type) {
            let type =
              policy?.type === 1 ? "%" : policy?.type === 2 ? "$" : false;

            let formattedDate = moment(policy?.dueDate).format("MM/DD/YYYY");

            if (type === "%") {
              return `${formattedDate} - ${policy?.value}${type}`;
            }

            if (type === "$") {
              let formattedValue = currencyFormatter.format(policy?.value);
              return `${formattedDate} - ${formattedValue}`;
            }
          }

          return [];
        }
      );

      return {
        operatorName: ofr?.operator?.name,
        fet: ofr?.fet?.label,
        dusCancellationPolicy: dusCancellationPolicy,
        remainingPenalties: remainingPenalties,
      };
    });

    let recipientName = `${state?.contractRecord?.clientData?.firstName} ${state?.contractRecord?.clientData?.lastName}`;
    let signerName = state?.contractRecord?.signerName
      ? `${state?.contractRecord?.signerName}`
      : false;
    let onBehalfOf = state?.contractRecord?.companyName
      ? `${state?.contractRecord?.companyName}`
      : false;

    let signerEmail = state?.contractRecord?.signerEmail || false;
    let totalClientPayment = currencyFormatter.format(
      state.contractRecord?.pricing?.flightCharges
    );

    let subtotal = currencyFormatter.format(
      state.contractRecord?.pricing?.flightCharges
    );

    //if ccFee is number and if state.contractRecord?.pricing?.paymentType is "cc"
    // add ccFee to  state.contractRecord?.pricing?.flightCharges, replace totalClientPayment

    if (
      state.contractRecord?.pricing?.ccFee &&
      state.contractRecord?.pricing?.paymentType === "cc" &&
      !isNaN(parseFloat(state.contractRecord?.pricing?.ccFee))
    ) {
      totalClientPayment = currencyFormatter.format(
        parseFloat(state.contractRecord?.pricing?.flightCharges) +
          parseFloat(state.contractRecord?.pricing?.ccFee)
      );
    }

    //set modalData
    dispatch({
      type: "fieldChanged",
      field: "modalData",
      value: {
        ...state.modalData,
        clientPricePreFees: currencyFormatter.format(
          state.contractRecord?.pricing?.flightCharges -
            state.contractRecord?.pricing?.ccFee
        ),
        ccFee: currencyFormatter.format(state.contractRecord?.pricing?.ccFee),
        operatorName: state.contractRecord?.legs[0]?.operator?.name,
        operatorPrice: "",
        totalClientPayment: currencyFormatter.format(
          state.contractRecord?.pricing?.flightCharges
        ),
        recipientDetail: {
          fullName: recipientName,
          email: state?.contractRecord?.clientData?.emails[0] || "",
          ccEmails: [],
          companyName: state?.contractRecord?.companyName || "",
          sendingNotes: "",
          signerName,
          onBehalfOf,
          signerEmail,
          recipientName: signerName || recipientName,
          recipientEmail:
            signerEmail || state?.contractRecord?.clientData?.emails[0] || "",
        },
        leadDesignation: {
          clientType: null,
          leadSource: null,
          firstContact: "",
          leadNotes: state.leadData.internalComments || null,
        },
        contractOFRDetail: {
          clientName: `${state?.contractRecord?.clientData?.firstName} ${state?.contractRecord?.clientData?.lastName}`,
          dates: legDates,
          routing: routingString,
          operatorBreakdown,
          contractNotes: "",
        },
        paymentDetail: {
          paymentType: state.contractRecord?.pricing?.paymentType,
          paymentNotes: "",
          totalClientPayment: totalClientPayment,
          clientPricePreFees: currencyFormatter.format(
            state.contractRecord?.pricing?.flightCharges -
              state.contractRecord?.pricing?.ccFee
          ),
          ccFee: currencyFormatter.format(state.contractRecord?.pricing?.ccFee),
          operatorPrices: selectedOFRs.map((ofr) => {
            return {
              operatorName: ofr?.operator?.name,
              operatorPrice: ofr?.operatorPrice,
            };
          }),
          payReferralFeeTo: "",
          referralFee: "",
          subtotal,
        },
      },
    });

    if (["markSentNotRequested", "syncToAppNotRequested", "requestToSend"].includes(action)) {
      //show requestToSend

      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value:
          action === "markSentNotRequested" ?
            "Mark Contract Sent" :
            action === "syncToAppNotRequested" ?
              "Sync to App & Mark Sent" :
              "Request To Send",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });
    }

    if (action === "requestCC") {
      //show cc modal

      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Request CC Approval",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });
    }

    if (
      action === "requestDollarApproval50k" ||
      action === "requestDollarApproval100k"
    ) {
      //show dollar approval

      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Request High Dollar Approval",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });
    }

    if (
      action === "highDollarApproval50k" ||
      action === "highDollarApproval100k"
    ) {
      //show dollar approval

      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Approve High Dollar Contract",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });

      dispatch({
        type: "fieldChanged",
        field: "modalButtonText",
        value: "Approve",
      });
    }

    if (action === "markSent") {
      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Mark Contract Sent",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });

      dispatch({
        type: "fieldChanged",
        field: "modalButtonText",
        value: "Mark Sent",
      });
    }

    if (action === "markSigned") {
      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Mark Contract Signed",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });

      dispatch({
        type: "fieldChanged",
        field: "modalButtonText",
        value: "Mark Signed",
      });
    }

    if (action === "revokeSendRequest") {
      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Revoke Send Request",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: action,
      });

      dispatch({
        type: "fieldChanged",
        field: "modalButtonText",
        value: "Revoke",
      });
    }

    if (action === "syncToAppAndMarkSent") {
      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Sync to App & Mark Sent",
      });

      dispatch({
        type: "fieldChanged",
        field: "action",
        value: "markSent",
      });

      dispatch({
        type: "fieldChanged",
        field: "modalButtonText",
        value: "Mark Contract Sent",
      });

      dispatch({
        type: "fieldChanged",
        field: "showModal",
        value: true,
      });

      return true;
    }

    if (action === "archiveEvoContract") {
      dispatch({
        type: "fieldChanged",
        field: "modalTitle",
        value: "Archive Evo Contract",
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "showModal",
      value: true,
    });

    return true;
  };

  const handleContractActionButton = async (event) => {
    event.preventDefault();

    let action = event.target.name
    if (action === "markSent" && state.contractRecord?.stage !== "inReview") {
      action = "markSentNotRequested"
    }
    dispatch({
      type: "fieldChanged",
      field: "action",
      value: action,
    });

    let modalResponse = handleContractModal(action);

    if (modalResponse) {
      return;
    }

    if (action === 'markUnsigned') {
      dispatch(
        {
          type: "fieldChanged",
          field: "isMarkUnsignedUpdating",
          value: true,
        }
      );
    }

    let response;
    try {
      response = await handleActionSubmit(action);
    } finally {
      dispatch(
        {
          type: "fieldChanged",
          field: "isMarkUnsignedUpdating",
          value: false,
        }
      );
    }

    return response;
  };

  useEffect(() => {
    getData();
    document.title = "Contract Preview | evoJets HQ";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModalSelectChange = (selectedValue, action) => {
    dispatch({
      type: "modalFieldChanged",
      field: action.name,
      value: selectedValue.value,
    });
  };

  const handleModalFormSubmission = async (e) => {
    e.preventDefault();

    if (
      ["markSentNotRequested", "syncToAppNotRequested", "requestToSend"].includes(state.action) &&
      (
        !state.selectedOFRs?.length ||
        !state.modalData?.leadDesignation?.clientType ||
        (state.modalData?.leadDesignation?.clientType === "New" &&
          (!state.modalData?.leadDesignation?.firstContact || !state.modalData?.leadDesignation?.leadSource)
        )
      )
    ) {
      toast.error(
        "Please fill in all required fields and retry.",
      );
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isUpdating",
      value: true,
    });

    try {
      await handleActionSubmit(state.action);
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "isUpdating",
        value: false,
      });
    }

    return true;
  };

  const handleSyncToApp = async () => {
    if (isSaved) {
      handleContractModal("syncToAppNotRequested");
      return;
    }

    dispatch(
      {
        type: "fieldChanged",
        field: "isSyncingToApp",
        value: true,
      }
    );

    try {
      const token = await getAccessTokenSilently().catch((e) => {
        console.error(e);
        return loginWithRedirect();
      });

      const response = await ai
        .auth(token)
        .patch(`/api/trips/${tripId}/contracts/${contractId}/syncToApp`)
        .catch((error) => {
          toast.error(
            getApiErrorMessage(error, "Error syncing to app!"),
            toastOptions
          );
          console.error(error.response);
        });

      if (response?.data) {
        handleContractModal("syncToAppAndMarkSent");
        toast.success("Contract synced to app successfully!", toastOptions);
      }
    } catch (error) {
      console.error("Error syncing to app:", error);
      toast.error("Error syncing to app!", toastOptions);
    } finally {
      dispatch(
        {
          type: "fieldChanged",
          field: "isSyncingToApp",
          value: false,
        }
      );
    }
  };

  const handleActionSubmit = async (action) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let response = await ai
      .auth(token)
      .put(`/api/trips/${tripId}/contracts/${contractId}/action`, {
        action: action,
        actionData: state.modalData,
      })
      .catch((error) => {
        setTimeout(() => {
          toast.error(
            getApiErrorMessage(error, "Unable to update the contract."),
            toastOptions
          );
        }, 50);

        return console.error(error.response);
      });

    if (response?.data && response?.data?.message) {
      toast.success(response?.data?.message, toastOptions);

      await getData(true);

      //close modal
      dispatch({
        type: "fieldChanged",
        field: "showModal",
        value: false,
      });
    }
  };

  const {
    isCCTrip,
    is50KTrip,
    is100KTrip,
    isHighDollarTrip,
    ofrExists,
    isSent,
    isSigned,
    hasRetrievableSignedPdf,
    isSaved,
    isCCApproved,
    isInReview,
    isFOW,
    isCTF,
    isAppManagedContract,
    isSafeToSend,
    contractInstructions,
    isHighDollarApproved,
    requiresCCApproval,
    canRequestDollarApproval,
    canMarkSent,
  } = state.logic;

  return (
    <React.Fragment>
      {state.loading && <Loader />}

      {!state.loading && (
        <React.Fragment>
          <PageHeader
            pageTitle="Preview Contract"
            breadcrumbs={[
              {link: '/trips', label: 'Trips'},
              {link: `/trips/${tripId}`, label: `Trip #${state.tripTag}`},
              {label: `Preview Contract #${state.contractRecord?.contractTag}`, isCurrentPage: true}
            ]}
            goBackLink={`/trips/${tripId}`}
            extraActions={
              <div className="d-flex flex-wrap flex-align-center">
                {!isSigned && !readOnly && (
                  <Link
                    className="base-button icon-button evo-blue"
                    to={`/trips/${tripId}/contracts/${contractId}`}
                    title="Edit Contract"
                  >
                    <span className="material-icons">edit</span>
                  </Link>
                )}
                {!ofrExists && !readOnly && (
                  <Link
                    className="base-button icon-button evo-blue ml-2"
                    to={`/trips/${tripId}/financials/new`}
                    title="Create OFR"
                  >
                    <span className="material-icons">note_add</span>
                  </Link>
                )}
                {(ofrExists || superAdmin || isSigned) && (
                  <button
                    className="base-button icon-button evo-blue ml-2"
                    onClick={handlePDF}
                    title="Save as PDF"
                  >
                    <span className="material-icons">cloud_download</span>
                  </button>
                )}
                {hasRetrievableSignedPdf && (
                  <button
                    className="base-button icon-button evo-blue ml-2"
                    onClick={handleSignedPDF}
                    title="Download Signed Contract"
                  >
                    <span className="material-icons">verified</span>
                  </button>
                )}
              </div>
            }
          />

          {!readOnly && (
          <>
          <hr />

          <h4>Contract Requests</h4>

          {contractInstructions && contractInstructions}

          <div className="d-flex flex-wrap flex-align-center gap-20">
            {isSafeToSend && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name="requestToSend"
              >
                Request to Send
              </button>
            )}

            {canRequestDollarApproval && ofrExists && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name={is100KTrip ? "requestDollarApproval100k" : "requestDollarApproval50k"}
              >
                Request Dollar Approval
              </button>
            )}
            {isCCTrip && !isCCApproved && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name="requestCC"
              >
                Request CC Approval
              </button>
            )}
          </div>

          <hr />

          <h4>Contract Stage Changes</h4>

          <div className="d-flex flex-wrap flex-align-center gap-20">
            {isHighDollarApproved &&
              ofrExists &&
              canMarkSent &&
              !requiresCCApproval &&
              !isSent &&
              !isAppManagedContract && (
                <button
                  className="base-button evo-dark-green "
                  onClick={handleContractActionButton}
                  name="markSent"
                >
                  Mark Sent
                </button>
              )}

            {isHighDollarApproved &&
              ofrExists &&
              canMarkSent &&
              (isInReview || isSaved) &&
              !requiresCCApproval && (
                <LoadingButton
                  className="base-button evo-blue"
                  onClick={handleSyncToApp}
                  name="syncToApp"
                  isLoading={state.isSyncingToApp}
                >
                  Save & Sync To App
                </LoadingButton>
              )}

            {isSent && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name="markSigned"
              >
                Mark Signed
              </button>
            )}

            {isSigned && !isAppManagedContract && (
              <React.Fragment>
                <LoadingButton
                  className="base-button evo-dark-green"
                  onClick={handleContractActionButton}
                  name="markUnsigned"
                  isLoading={state.isMarkUnsignedUpdating}
                >
                  Mark Unsigned
                </LoadingButton>
                <button
                  className="base-button evo-dark-green "
                  onClick={handleContractActionButton}
                  name="cancelEvoContract"
                >
                  Cancel Evo Contract
                </button>
              </React.Fragment>
            )}

            {isSaved && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name="archiveEvoContract"
              >
                Archive Evo Contract
              </button>
            )}

            {isInReview && (
              <button
                className="base-button evo-dark-green "
                onClick={handleContractActionButton}
                name="revokeSendRequest"
              >
                Revoke Send Request
              </button>
            )}
          </div>

          {(((isCCTrip && !isCCApproved) && (superAdmin || isCcApprover)) ||
            ((isHighDollarTrip && !isHighDollarApproved) &&
              (is100KTrip ? isHighDollarApprover100k : isHighDollarApprover))) && (
              <React.Fragment>
                <hr />

                <h4>Contract Approvals</h4>

                <div className="d-flex flex-wrap flex-align-center gap-20">
                  {isCCTrip && !isCCApproved && (superAdmin || isCcApprover) && (
                    <button
                      className="base-button evo-dark-green "
                      onClick={handleContractActionButton}
                      name="ccApproval"
                    >
                      Approve CC
                    </button>
                  )}

                  {is50KTrip && isHighDollarApprover && !isHighDollarApproved && (
                    <button
                      className="base-button evo-dark-green "
                      onClick={handleContractActionButton}
                      name={"highDollarApproval50k"}
                    >
                      Approve Dollar Amount
                    </button>
                  )}

                  {isHighDollarApprover100k &&
                    is100KTrip &&
                    !isHighDollarApproved && (
                      <button
                        className="base-button evo-dark-green "
                        onClick={handleContractActionButton}
                        name={"highDollarApproval100k"}
                      >
                        Approve Dollar Amount
                      </button>
                    )}
                </div>
              </React.Fragment>
            )}
          </>
          )}

          <hr />
        </React.Fragment>
      )}

      <ReactModal
        isOpen={state.showModal}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          dispatch({
            type: "fieldChanged",
            field: "showModal",
            value: false,
          });
        }}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "90vh",
            minWidth: "calc(min(600px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header edit-note-modal">
          <h4>{state.modalTitle}</h4>
        </div>

        <div className="modal-body edit-note-modal modal-body-with-scroll">
          <form
            onSubmit={handleModalFormSubmission}
            className="w-100 base-form mt-20 tight-paragraphs"
          >
            {[
              "requestToSend",
              "markSentNotRequested",
              "syncToAppNotRequested",
              "requestCC",
              "requestDollarApproval100k",
              "requestDollarApproval50k",
            ].includes(state.action) &&
              state.contractRecord?.operatorFinancialRecords?.length > 0 && (
                <React.Fragment>
                  <h5>Associated OFR(s) <span className="required">*</span></h5>

                  <div className="form-group">
                    <Select
                      className="base-select yom-select"
                      isClearable
                      isSearchable
                      placeholder={"Select All Applicable OFRs"}
                      name="associatedOFRs"
                      onChange={(selectedValue, action) => {
                        let ids = [];

                        if (selectedValue?.length) {
                          ids = selectedValue.map((value) => {
                            return value.value;
                          });
                        }
                        dispatch({
                          type: "modalFieldChanged",
                          field: "associatedOFRIds",
                          value: ids,
                        });

                        dispatch({
                          type: "fieldChanged",
                          field: "selectedOFRs",
                          value: selectedValue || [],
                        });
                      }}
                      isMulti
                      options={state.contractRecord?.operatorFinancialRecords.map(
                        (ofr) => {
                          return {
                            label: `#${ofr?.ofrTag} | ${
                              ofr?.operator?.name
                            } | ${
                              ofr?.operatorPrice
                                ? currencyFormatter.format(ofr?.operatorPrice)
                                : "No Operator Price"
                            }`,
                            value: ofr?._id,
                          };
                        }
                      )}
                      value={state.selectedOFRs}
                    />
                  </div>
                </React.Fragment>
              )}

            {["markSentNotRequested", "syncToAppNotRequested", "requestToSend"].includes(state.action) && (
              <React.Fragment>
                <h5>RECIPIENT DETAIL</h5>
                <p>
                  <strong>Recipient Name: </strong>
                  {state.modalData?.recipientDetail?.recipientName}
                </p>
                {state.modalData?.recipientDetail?.onBehalfOf && (
                  <p>
                    <strong>On behalf of: </strong>
                    {state.modalData?.recipientDetail?.onBehalfOf}
                  </p>
                )}

                <p>
                  <strong>Recipient Email: </strong>
                  {state.modalData?.recipientDetail?.recipientEmail}
                </p>

                <EmailInput
                  parentDispatch={dispatch}
                  dispatchType={"recipientDetailFieldChanged"}
                  dispatchField={"ccEmails"}
                  placeholder="Enter CC Email(s)"
                  label="CC Email(s)"
                  name="ccEmails"
                ></EmailInput>
                <TextAreaInput
                  handleChange={(e) => {
                    dispatch({
                      type: "recipientDetailFieldChanged",
                      field: "sendingNotes",
                      value: e.target.value,
                    });
                  }}
                  value={state?.modalData?.recipientDetail?.sendingNotes || ""}
                  label="Sending Notes"
                />
                <h5>LEAD DESIGNATION</h5>
                <div className="form-group">
                  <label className="base-label">
                    Client Type <span className="required">*</span>
                  </label>
                  <Select
                    className="base-select clientType-select"
                    classNamePrefix="select"
                    options={[
                      {
                        label: "New",
                        value: "New",
                      },
                      {
                        label: "Repeat",
                        value: "Repeat",
                      },
                    ]}
                    placeholder="Client Type"
                    onChange={(selectedValue) => {
                      dispatch({
                        type: "leadDesignationFieldChanged",
                        field: "clientType",
                        value: selectedValue.value,
                      });
                    }}
                    key={`key-client-type`}
                    name="clientType"
                  />

                  <h5 className="mt-20">Contract / OFR Detail</h5>

                  <p>
                    <strong>Client Name: </strong>
                    {state.modalData?.contractOFRDetail?.clientName}
                  </p>

                  <p>
                    <strong>Dates: </strong>
                    {state.modalData?.contractOFRDetail?.dates}
                  </p>

                  <p>
                    <strong>Routing: </strong>
                    {state.modalData?.contractOFRDetail?.routing}
                  </p>

                  {state.modalData?.contractOFRDetail?.operatorBreakdown?.map(
                    (penalty, index) => {
                      return (
                        <p key={index}>
                          <strong>Operator {index + 1} Name:</strong>{" "}
                          {penalty?.operatorName} <br />
                          <strong>FET:</strong> {penalty?.fet || "No FET"} <br />
                          <strong>Fee Upon Booking:</strong>{" "}
                          {penalty?.dusCancellationPolicy} <br />
                          <strong>Next Penalty:</strong>{" "}
                          {penalty?.remainingPenalties?.length
                            ? penalty?.remainingPenalties.join(", ")
                            : "No Remaining Penalties"}
                        </p>
                      );
                    }
                  )}

                  <TextAreaInput
                    handleChange={(e) => {
                      dispatch({
                        type: "contractOFRDetailFieldChanged",
                        field: "contractNotes",
                        value: e.target.value,
                      });
                    }}
                    value={
                      state?.modalData?.contractOFRDetail?.contractNotes || ""
                    }
                    label="Contract Notes"
                  />
                </div>
                {state.modalData?.leadDesignation?.clientType === "New" && (
                  <React.Fragment>
                    <div className="form-group">
                      <label className="base-label">
                        Lead Source <span className="required">*</span>
                      </label>
                      <Select
                        className="clientType-select"
                        classNamePrefix="select"
                        options={[
                          {
                            label: "RFP",
                            value: "RFP",
                          },
                          {
                            label: "Phone Call",
                            value: "Phone Call",
                          },
                          {
                            label: "Outreach Sequence",
                            value: "Outreach Sequence",
                          },
                          {
                            label: "UpLead",
                            value: "UpLead",
                          },
                          {
                            label: "Email In",
                            value: "Email In",
                          },
                          {
                            label: "Personal Network",
                            value: "Personal Network",
                          },
                          {
                            label: "Other",
                            value: "Other",
                          },
                        ]}
                        placeholder="Lead Source"
                        onChange={(selectedValue) => {
                          dispatch({
                            type: "leadDesignationFieldChanged",
                            field: "leadSource",
                            value: selectedValue.value,
                          });
                        }}
                        key={`key-client-type`}
                        name="clientType"
                      />
                    </div>
                    <div className="datepickerContainer mb-0">
                      <label
                        htmlFor={`firstContactDate`}
                        className="base-label"
                      >
                        First Contact <span className="required">*</span>
                      </label>

                      <Datepicker
                        value={state?.modalData?.leadDesignation?.firstContact}
                        onChange={(event) => {
                          dispatch({
                            type: "leadDesignationFieldChanged",
                            field: "firstContact",
                            value: event.value,
                          });
                        }}
                        dataTimezone="utc"
                        displayTimezone="local"
                        timezonePlugin={momentTimezone}
                        controls={["date"]}
                      />
                    </div>
                  </React.Fragment>
                )}
                <h5 className="mt-20">Payment Detail</h5>
                <p>
                  <strong>Payment Type: </strong>
                  {state.modalData?.paymentDetail?.paymentType === "cc"
                    ? "CC"
                    : state.modalData?.paymentDetail?.paymentType === "wire"
                    ? "Wire"
                    : "-"}
                </p>
                <p>
                  <strong>Total Client Payment: </strong>
                  {state.modalData?.paymentDetail?.totalClientPayment}
                </p>
                {state.modalData?.paymentDetail?.ccFee &&
                  state.modalData?.paymentDetail?.paymentType === "cc" && (
                    <p>
                      <strong>CC Fees:</strong>{" "}
                      {state.modalData?.paymentDetail?.ccFee}
                    </p>
                  )}
                {state.modalData?.paymentDetail?.operatorPrices?.map(
                  (price, index) => {
                    return (
                      <p key={index}>
                        <strong>Operator {index + 1}:</strong>{" "}
                        {price?.operatorName} -{" "}
                        {currencyFormatter.format(price?.operatorPrice)}
                      </p>
                    );
                  }
                )}
                {/* text input for pay referral fee to */}
                <TextInput
                  handleChange={(e) => {
                    dispatch({
                      type: "paymentDetailFieldChanged",
                      field: "payReferralFeeTo",
                      value: e.target.value,
                    });
                  }}
                  value={
                    state?.modalData?.paymentDetail?.payReferralFeeTo || ""
                  }
                  label="Pay Referral Fee To"
                />
                <div className="form-group">
                  <label>Referral Fee</label>

                  <NumberFormat
                    value={state?.modalData?.paymentDetail?.referralFee || ""}
                    thousandSeparator={true}
                    prefix={"$"}
                    onValueChange={(values, sourceInfo) => {
                      const { value } = values;

                      dispatch({
                        type: "paymentDetailFieldChanged",
                        field: "referralFee",
                        value: value,
                      });
                    }}
                    placeholder="Referral Fee"
                    className="semibold-text"
                    decimalScale={2}
                    name="referralFee"
                  />
                </div>
                <TextAreaInput
                  handleChange={(e) => {
                    dispatch({
                      type: "paymentDetailFieldChanged",
                      field: "paymentNotes",
                      value: e.target.value,
                    });
                  }}
                  value={state?.modalData?.paymentDetail?.paymentNotes || ""}
                  label="Payment Notes"
                />
              </React.Fragment>
            )}

            {!["markSentNotRequested", "syncToAppNotRequested", "requestToSend"].includes(state.action) && (
              <React.Fragment>
                <h5>TRIP DATA</h5>

                <p>
                  <strong>Total Client Payment: </strong>
                  {state.modalData?.totalClientPayment}
                </p>
              </React.Fragment>
            )}
            {isCCTrip && !["markSentNotRequested", "syncToAppNotRequested", "requestToSend"].includes(state.action) && (
              <div>
                <p>
                  <strong>Subtotal (Pre-CC Fees):</strong>{" "}
                  {state.modalData?.subtotal}
                </p>
                <p>
                  <strong>CC Fees:</strong> {state.modalData?.ccFee}
                </p>
                <p>
                  <strong>Total:</strong> {state.modalData?.totalClientPayment}
                </p>
              </div>
            )}
            {state.action === "requestCC" && (
              <React.Fragment>
                <div className="form-group ml-0 mt-20">
                  <label className="base-label">Client Type</label>
                  <Select
                    className="clientType-select"
                    classNamePrefix="select"
                    menuPosition="fixed"
                    options={[
                      {
                        label: "New",
                        value: "New",
                      },
                      {
                        label: "Repeat",
                        value: "Repeat",
                      },
                    ]}
                    placeholder="Client Type"
                    onChange={handleModalSelectChange}
                    key={`key-client-type`}
                    name="clientType"
                  />
                </div>
              </React.Fragment>
            )}

            {(state.action === "requestDollarApproval50k" ||
              state.action === "requestDollarApproval100k") && (
              <div className="form-group">
                <label className="base-label" htmlFor="note">
                  Request Notes
                </label>
                <textarea
                  name="notes"
                  id="notes"
                  cols="30"
                  rows="3"
                  value={state?.modalData?.notes || ""}
                  onChange={(e) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "modalData",
                      value: {
                        ...state.modalData,
                        notes: e.target.value,
                      },
                    });
                  }}
                />
              </div>
            )}
          </form>
        </div>

        <div className="modal-footer w-100 mt-10">
          <LoadingButton
            className="base-button evo-blue w-100"
            type="submit"
            onClick={handleModalFormSubmission}
            isLoading={state.isUpdating}
          >
            {state.modalButtonText ? state.modalButtonText : "Submit"}
          </LoadingButton>

          <button
            className="base-button btn-danger w-100 mt-10"
            type="button"
            onClick={() => {
              dispatch({
                type: "fieldChanged",
                field: "showModal",
                value: false,
              });
            }}
          >
            Cancel
          </button>
        </div>
      </ReactModal>

      <iframe
        className="contract-preview-container"
        id="contractPreviewIframe"
        sandbox="allow-same-origin"
        title="Contract Preview"
        srcDoc={state.contractHTML}
      />

      <StateDisplay data={state} user={user} />
    </React.Fragment>
  );
};

export default ContractPreviewContent;
